import React from "react";
import { FaSearch } from "react-icons/fa";
import { GUEST_FIELD_LABEL_CLASS } from "../guestVezoUtils";

const SEARCH_MIN_CHARS = 3;

/**
 * Flat provider list; search uses server fetch when query length ≥ SEARCH_MIN_CHARS.
 */
export default function GuestProvidersPanel({
  displayRows = [],
  providersLoading = false,
  hasLocation = false,
  /** Rows from last full (no-keyword) fetch for this location */
  cachedProviderRowCount = 0,
  /** True after initial provider fetch for current location finished */
  providersInitialDone = false,
  providerSearch = "",
  onProviderSearchChange,
  selectedProvider = null,
  onSelectProvider,
  className = "",
}) {
  const q = providerSearch?.trim() ?? "";
  const keywordActive = q.length >= SEARCH_MIN_CHARS;
  const searchTyping = q.length > 0 && !keywordActive;

  const header = (
    <div className="shrink-0">
      <label htmlFor="guest-provider-search" className={GUEST_FIELD_LABEL_CLASS}>
        Provider
      </label>
      <div className="relative mt-2">
        <FaSearch
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          size={13}
          aria-hidden
        />
        <input
          type="search"
          id="guest-provider-search"
          name="guest-provider-search"
          value={providerSearch}
          onChange={(e) => onProviderSearchChange?.(e.target.value)}
          placeholder={`Type ${SEARCH_MIN_CHARS}+ letters to search`}
          disabled={!hasLocation}
          autoComplete="off"
          className="w-full min-h-[2.5rem] rounded-lg border border-slate-300 bg-white py-2 pl-8 pr-2.5 text-base text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60 sm:text-sm"
        />
      </div>
      {hasLocation && !keywordActive ? (
        <p className="mt-1 text-[11px] leading-snug text-slate-400">
          Full list below. Type {SEARCH_MIN_CHARS}+ letters to search by name.
        </p>
      ) : null}
    </div>
  );

  const listArea = (
    <div className="mt-2 min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
      {!hasLocation ? (
        <p className="rounded-lg bg-slate-50 px-2 py-3 text-center text-xs text-slate-500 sm:text-sm">
          Select a location above to load providers.
        </p>
      ) : !providersInitialDone && providersLoading ? (
        <p className="py-4 text-center text-sm text-slate-600">Loading providers…</p>
      ) : providersInitialDone && cachedProviderRowCount === 0 && !keywordActive ? (
        <p className="py-3 text-center text-xs text-slate-500 sm:text-sm">No providers for this location.</p>
      ) : keywordActive && providersLoading ? (
        <p className="py-4 text-center text-sm text-slate-600">Searching providers…</p>
      ) : keywordActive && !providersLoading && displayRows.length === 0 ? (
        <p className="py-3 text-center text-xs text-slate-500 sm:text-sm">No providers match that search.</p>
      ) : displayRows.length === 0 && searchTyping ? (
        <p className="py-3 text-center text-xs text-slate-500 sm:text-sm">
          Keep typing — search runs after {SEARCH_MIN_CHARS} letters.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-slate-50/80">
          {displayRows.map((row) => {
            const isSel = selectedProvider?.provider_id === row.provider_id;

            return (
              <li key={row.provider_id}>
                <button
                  type="button"
                  onClick={() => onSelectProvider?.(row)}
                  className={`flex w-full min-h-[2.75rem] flex-col items-start justify-center px-2.5 py-2.5 text-left transition-colors active:bg-white sm:min-h-[3rem] sm:px-3 ${
                    isSel
                        ? "bg-teal-50 ring-1 ring-inset ring-teal-200"
                        : "hover:bg-white"
                  }`}
                >
                  <span
                    className={`text-sm sm:text-[15px] ${isSel ? "font-semibold text-slate-900" : "font-medium text-slate-800"}`}
                  >
                    {row.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className={`flex min-h-0 flex-col overflow-hidden ${className}`} aria-labelledby="guest-providers-heading">
      <span id="guest-providers-heading" className="sr-only">
        Provider search and list
      </span>
      {/* {header} */}
      {listArea}
    </div>
  );
}
